/**
 * outcomeLabels — display strings for `KanbanCardOutcome` enum values (SE-5496).
 *
 * The enum is a fixed, board-template-scoped vocabulary
 * (`board.validOutcomes`); this maps each machine value to a human label for
 * the terminal-outcome selector and the Closed-area group headers.
 *
 * AC2 ("outcome labels can be renamed without losing history") is a BACKEND
 * guarantee: a club renaming an outcome label does not mutate any closed card's
 * stored `outcome`/`closedAt`. This module only renders the current enum value
 * — it never writes, so there is no history to lose here. When the backend
 * ships per-club custom labels, this is the single seam that would read them.
 * @module features/player-kanban/utils/outcomeLabels
 */

import { KanbanCardOutcome } from "@/generated/graphql";

/**
 * Human-readable label for each terminal outcome. Exhaustive over the enum so
 * a newly-added outcome value is a compile error here rather than a silent
 * raw-enum leak into the UI.
 */
const OUTCOME_LABELS: Record<KanbanCardOutcome, string> = {
  [KanbanCardOutcome.Signed]: "Signed",
  [KanbanCardOutcome.Passed]: "Passed",
  [KanbanCardOutcome.Sold]: "Sold",
  [KanbanCardOutcome.Promoted]: "Promoted",
  [KanbanCardOutcome.Returned]: "Returned",
  [KanbanCardOutcome.Withdrawn]: "Withdrawn",
};

/**
 * Resolve the display label for a terminal outcome.
 * @param outcome - The terminal outcome enum value.
 * @returns The human-readable label (e.g. `SIGNED` → "Signed").
 */
export const outcomeLabel = (outcome: KanbanCardOutcome): string =>
  OUTCOME_LABELS[outcome];
